import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  StitchingManipulationRules,
  FabricObjectWithSrc,
  ChangableFabricProps,
  mapRawToFabricImages,
  extractFabricObjectMovementProperties
} from '../../../shared/Helpers';
import { SequenceImage } from '../../StitchingTool/Stitching/Stitching.reducer';

let numberOfKeyEvents = 0;

type CanvasSizeChanged = {
  canvasWidth: number;
  canvasHeight: number;
};

type Updated = {
  rawImages: SequenceImage[] | null;
  isStitched: boolean;
  subsampleFactor: number;
};

type AddToHistory = {
  activeObjects: fabric.Object[];
  skipSmallMovements: boolean;
};

export type State = {
  fabricImages: Array<FabricObjectWithSrc>;
  presentFabricImages: FabricObjectWithSrc[];
  rawImages: any[];
  leftScale: number;
  centringLeftScale: number;
  topScale: number;
  canvasWidth: number | null;
  canvasHeight: number | null;
  isStitched: boolean;
  stitchingManipulationRules: StitchingManipulationRules;
  zoom: number;
  pastFabricImages: FabricObjectWithSrc[][];
  futureFabricImages: FabricObjectWithSrc[][];
  reRender: boolean;
};

let initialState: State = {
  fabricImages: [],
  presentFabricImages: [],
  rawImages: [],
  pastFabricImages: [],
  futureFabricImages: [],
  stitchingManipulationRules: {
    lockMovementX: true,
    lockMovementY: true,
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true
  },
  isStitched: false,
  leftScale: 0,
  topScale: 0,
  centringLeftScale: 0,
  canvasHeight: null,
  canvasWidth: null,
  zoom: 1,
  reRender: false
};

const updatePastState = (state: any, updatedImagePropsArray: ChangableFabricProps[]) => {
  const { pastFabricImages, presentFabricImages } = state;

  let updatedFabricImages = [...presentFabricImages];

  for (const updatedImageProps of updatedImagePropsArray) {
    updatedFabricImages = updateLastFabricImages(updatedFabricImages, updatedImageProps);
  }

  state.pastFabricImages = [...pastFabricImages, [...presentFabricImages]];
  state.presentFabricImages = updatedFabricImages;
  state.futureFabricImages = [];
  state.reRender = false;
};

const updateLastFabricImages = (lastFabricImages: FabricObjectWithSrc[], updatedImageProps: ChangableFabricProps) => {
  const fabricImageToUpdate = lastFabricImages.find(f => f.name === updatedImageProps.name);

  if (fabricImageToUpdate) {
    return [
      ...lastFabricImages.filter(f => f.name !== updatedImageProps.name),
      {
        ...fabricImageToUpdate,
        ...updatedImageProps
      }
    ];
  }
  return [...lastFabricImages];
};

const calculateZoom = (images: Array<FabricObjectWithSrc>, canvasHeight: number) => {
  if (images.length === 0) return 1;

  const maxTopPostion = Math.max(...images.map(i => i.top)) + images[images.length - 1].height; // + 50;

  const zoom = maxTopPostion / canvasHeight;

  return zoom;
};

const stitchedViewSlice = createSlice({
  name: 'stitchedView',
  initialState,
  reducers: {
    resetState() {
      return initialState;
    },
    addToHistory(state, action: PayloadAction<AddToHistory>) {
      const { activeObjects, skipSmallMovements } = action.payload;

      const updatedObjects = [extractFabricObjectMovementProperties(activeObjects[0] as any)];

      if (skipSmallMovements) {
        numberOfKeyEvents++;
      }

      const canUpdate = !skipSmallMovements || numberOfKeyEvents % 5 === 1;

      if (canUpdate) {
        updatePastState(state, updatedObjects);
      }
    },
    sizeChanged(state, action: PayloadAction<CanvasSizeChanged>) {
      const { canvasHeight, canvasWidth } = action.payload;
      const { fabricImages } = state;

      const zoom = calculateZoom(Array.from(fabricImages), canvasHeight);

      state.canvasHeight = canvasHeight;
      state.canvasWidth = canvasWidth;
      state.zoom = zoom;
    },
    updated(state, action: PayloadAction<Updated>) {
      const { isStitched, rawImages, subsampleFactor } = action.payload;
      const { canvasHeight, canvasWidth } = state;

      if (rawImages && canvasWidth && canvasHeight) {
        const mappedData = mapRawToFabricImages({
          rawImages,
          isStitched,
          canvasHeight,
          canvasWidth,
          subsampleFactor
        });

        state.fabricImages = mappedData.fabricImages;
        state.topScale = mappedData.topScale;
        state.leftScale = mappedData.leftScale;
        state.centringLeftScale = mappedData.centringLeftScale;
        state.zoom = mappedData.zoom;
        state.isStitched = isStitched;
        state.rawImages = rawImages;
        state.fabricImages = mappedData.fabricImages;
        state.presentFabricImages = mappedData.fabricImages;
        state.pastFabricImages = [];
        state.futureFabricImages = [];
        state.reRender = false;
      }
    }
  }
});

export const { addToHistory, sizeChanged, updated, resetState } = stitchedViewSlice.actions;

export default stitchedViewSlice.reducer;
